import WhatsNewBell from "@/shared/components/WhatsNew/WhatsNewBell";
import React, { useMemo, useState } from "react";
// import CommonCardLayout from "./components/CommonCardLayout";
import { Clipboard, Link2, List } from "lucide-react";
import DashboardHeader from "./components/DashboardHeader";
import { useMainStore } from "@/stores";
import { formatDate } from "@/shared/utils/helperFunctions";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import DashboardCommonBgCard from "./components/common/DashboardCommonBgCard";
import ChannelEffectiveness from "./components/channelEffectiveness/ChannelEffectiveness";
import TopPerformingCampaigns from "./components/topCampaigns/TopPerformingCampaigns";
import CampaignPerformance from "./components/CampaignPerformance";
// import FreshIntent from "./components/accountsDashboard/freshIntentSignals/FreshIntent";
import Dormant from "./components/accountsDashboard/dormant/Dormant";
import TopEngaged from "./components/accountsDashboard/topEngaged/TopEngaged";
import FollowUp from "./components/accountsDashboard/followUp/FollowUp";
import SimilarAccounts from "./components/accountsDashboard/similarAccounts/SimilarAccounts";
import {
  useGetAccountsDashboard,
  useGetSimilarAccountTargets,
} from "./hooks/useGetDashboardApi";
import DealsSummary from "./components/DealsSummary";
// import BuyingSignalsResearch from "./components/BuyingSignalsResearch";
import SallyWidget from "@/features/sally/SallyWidget";
import {
  DATA_VIEW_OPTIONS,
  DAYS_MAP,
  // USER_DROPDOWN_OPTIONS,
} from "@/shared/utils/dashboardConfig";

const DashboardMainComponent = () => {
  const { user, sidebarExpanded } = useMainStore();
  const [accountsDataView, setAccountsDataView] = useState("last_30_days");
  const [campaignPerformanceDataView, setCampaignPerformanceDataView] =
    useState("last_30_days");
  const [topCampaignsDataView, setTopCampaignsDataView] =
    useState("last_30_days");
  const [channelDataView, setChannelDataView] = useState("last_30_days");
  // const [userDashboardView, setUserDashboardView] = useState("my_dashboard");

  const accountsDays = useMemo(
    () => DAYS_MAP[accountsDataView] ?? 30,
    [accountsDataView],
  );
  const campaignPerformanceDays = useMemo(
    () => DAYS_MAP[campaignPerformanceDataView] ?? 30,
    [campaignPerformanceDataView],
  );
  const topCampaignsDays = useMemo(
    () => DAYS_MAP[topCampaignsDataView] ?? 30,
    [topCampaignsDataView],
  );
  const channelDays = useMemo(
    () => DAYS_MAP[channelDataView] ?? 30,
    [channelDataView],
  );
  const { data: accountsData, isPending: isLoadingAccounts } =
    useGetAccountsDashboard(accountsDays);
  const { data: similarTargetData, isPending: isLoadingSimilarAccounts } =
    useGetSimilarAccountTargets(user?.id);
  const similarAccounts = similarTargetData?.similar_accounts ?? [];
  const similarReferenceAccounts = similarTargetData?.reference_accounts ?? [];
  const similarLastSyncedAt = similarTargetData?.last_synced_at ?? null;

  const renderDateSelect = (value, onChange, ariaLabel) => {
    const label =
      DATA_VIEW_OPTIONS.find((o) => o.value === value)?.label ?? "Last 30 Days";
    return (
      <CommonSelect
        ariaLabel={ariaLabel}
        options={DATA_VIEW_OPTIONS}
        value={value}
        placeholder={label}
        onChange={(selectedValue) => {
          if (selectedValue != null) onChange(String(selectedValue));
        }}
        width="w-[132px] min-w-[132px]"
        classNames={{
          base: "!rounded-lg w-full",
          mainWrapper: "!rounded-lg w-full",
          value: "!text-gray-800 dark:!text-gray-100 font-inter !font-medium !text-[0.68rem]",
        }}
      />
    );
  };
  // const userDashboardLabel =
  //   USER_DROPDOWN_OPTIONS.find((o) => o.value === userDashboardView)?.label ??
  //   "My Dashboard";

  return (
    <div className="flex flex-col gap-4 sm:gap-6 pt-1 sm:pl-4">
      {/* Header Section */}
      {/* Same right padding as the content below, so the actions line up with the cards. */}
      <div className="flex flex-col lg:flex-row sm:items-center sm:justify-between gap-4 w-full pr-1 sm:pr-4">
        <DashboardHeader
          title="Dashboard"
          userName={user?.name}
          role={user?.role}
          subtitle="See top leads, send personalized emails, and track success—all in one place."
        />

        {/* Right Actions */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 w-full sm:w-auto">
          {user?.trial_ends_at !== null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Your {user?.plan === "trial" ? "Trial" : "Plan"} ends on{" "}
              {formatDate(user?.trial_ends_at)}
            </p>
          )}

          <SecondaryButton
            title="Upgrade Now"
            className="border-none text-secondary dark:text-secondary-light bg-secondary/10 dark:bg-secondary/20 hover:bg-secondary/20 cursor-pointer text-xs sm:text-[0.695rem] w-full sm:w-auto font-medium"
            onClick={() => {
              window.open("https://salesworx.ai/salesworx-contact/", "_blank");
            }}
          />
          <WhatsNewBell compact className="hidden md:flex" />
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`${sidebarExpanded
          ? "max-h-[calc(100dvh-128px)]"
          : "max-h-[calc(100dvh-122px)]"
          } flex flex-col gap-5 overflow-y-auto pr-1 sm:pr-4`}
      >
        {/* Dashboard accounts */}
        <DashboardCommonBgCard
          title="Accounts"
          icon={<List size={18} strokeWidth={1.5} className="text-secondary" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 2xl:grid-cols-4 gap-4">
            <TopEngaged
              accounts={accountsData?.top_engaged_accounts ?? []}
              isLoading={isLoadingAccounts}
              headerAction={renderDateSelect(
                accountsDataView,
                setAccountsDataView,
                "Top engaged date range",
              )}
            />

            <Dormant
              accounts={accountsData?.dormant_accounts ?? []}
              reactivationAccounts={
                accountsData?.reactivation_opportunity_accounts ?? []
              }
              isLoading={isLoadingAccounts}
            />

            <SimilarAccounts
              accounts={similarAccounts}
              referenceAccounts={similarReferenceAccounts}
              lastSyncedAt={similarLastSyncedAt}
              isLoading={isLoadingAccounts || isLoadingSimilarAccounts}
            />

            <FollowUp
              followUps={accountsData?.upcoming_follow_ups ?? []}
              isLoading={isLoadingAccounts}
            />
          </div>
        </DashboardCommonBgCard>

        {/* Calendar section temporarily hidden; restore when the Outlook calendar is ready. */}
        {/*
        <DashboardCommonBgCard
          title="Upcoming Meetings"
          icon={<CalendarDays size={18} strokeWidth={1.5} className="text-secondary" />}
          tooltipContent="Upcoming Outlook and Teams meetings from your connected Microsoft calendar."
        >
          <CalendarWithEvents />
        </DashboardCommonBgCard>
        */}

        <DealsSummary />

        {/* Buying signals temporarily hidden from the main dashboard. */}
        {/* <BuyingSignalsResearch /> */}

        {/* Campaign insights */}
        <DashboardCommonBgCard
          title="Campaigns"
          icon={
            <Clipboard size={18} strokeWidth={1.5} className="text-secondary" />
          }
          tooltipContent="Track campaign performance based on engagement and conversions."
        >
          <div className="flex flex-col sm:flex-row gap-4 w-full items-stretch">
            {/* Campaign Performance */}
            <div className="w-full sm:w-[36%] xl:w-[34%] flex">
              <DashboardCommonBgCard
                title="Campaign Performance"
                headerAction={renderDateSelect(
                  campaignPerformanceDataView,
                  setCampaignPerformanceDataView,
                  "Campaign performance date range",
                )}
              >
                <CampaignPerformance days={campaignPerformanceDays} />
              </DashboardCommonBgCard>
            </div>

            {/* Top Performing Campaigns */}
            <div className="w-full sm:flex-1 flex">
              <DashboardCommonBgCard
                title="Top Performing Campaigns"
                stretch
                headerAction={renderDateSelect(
                  topCampaignsDataView,
                  setTopCampaignsDataView,
                  "Top performing campaigns date range",
                )}
              >
                <TopPerformingCampaigns days={topCampaignsDays} />
              </DashboardCommonBgCard>
            </div>
          </div>
        </DashboardCommonBgCard>

        {/* Channel effectiveness */}
        <DashboardCommonBgCard
          title="Channel Effectiveness"
          icon={
            <Link2 size={18} strokeWidth={1.5} className="text-secondary" />
          }
          headerAction={renderDateSelect(
            channelDataView,
            setChannelDataView,
            "Channel effectiveness date range",
          )}
        >
          <div className="overflow-x-auto">
            <ChannelEffectiveness days={channelDays} />
          </div>
        </DashboardCommonBgCard>
      </div>
      <SallyWidget />
    </div>
  );
};
export default DashboardMainComponent;
